import React from 'react'
import { forwardRef } from '@grupoboticario/flora-react-system'
import { FloraTextareaProps } from './types'
import { StyledTextarea, CharacterCount, MessagesWrapper } from './styles'
import { FloraHelperText } from '@grupoboticario/flora-react-helper-text'
import { Label, FocusWrapper } from './components'
import { useFloraTextarea } from './useFloraTextarea'

/**
 * @component
 * @name FloraInput
 * @description O componente FloraTextarea é um elemento de formulário que permite ao usuário inserir um texto de tamanho variável. Ele é usado em formulários onde o usuário precisa digitar um texto maior, como um comentário, um e-mail ou uma mensagem.
 * @tutorial https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floratextarea
 */
export const FloraTextarea = forwardRef<'textarea', FloraTextareaProps>(
  (
    {
      'autotagging-flora': autotaggingFlora = 'off',
      'data-flora-text': dataFloraText,
      'data-autotagging-flora-section': dataAutotaggingFloraSection = '',
      'aria-disabled': ariaDisabled,
      children,
      id,
      helperText,
      required,
      onChange,
      onBlur,
      onMouseDownCapture,
      status = 'neutral',
      maxLength,
      label,
      labelPosition = 'internal',
      backgroundStyle = 'default',
      placeholder,
      surfaceColor = 'light',
      disabled,
      readOnly,
      ...props
    },
    forwardedRef,
  ) => {
    const {
      charCount,
      ref,
      handleOnChange,
      handleOnMouseDownCaptureTextarea,
      handleOnMouseUpCaptureLabel,
      clicked,
      handleOnBlur,
    } = useFloraTextarea({
      forwardedRef,
      onChange,
      onBlur,
      onMouseDownCapture,
    })

    return (
      <>
        <FocusWrapper
          labelPosition={labelPosition}
          hasValue={charCount > 0}
          hasEffect={!disabled && !readOnly && !ariaDisabled && status !== 'loading'}
          surfaceColor={surfaceColor}
        >
          {label && (
            <Label
              htmlFor={id}
              labelPosition={labelPosition}
              surfaceColor={surfaceColor}
              placeholder={placeholder}
              required={required}
              status={status}
              disabled={disabled || !!ariaDisabled}
              onMouseUpCapture={handleOnMouseUpCaptureLabel}
            >
              {label}
            </Label>
          )}
          <StyledTextarea
            {...props}
            id={id}
            as="textarea"
            data-flora="textarea"
            data-flora-text={dataFloraText || label}
            data-autotagging-flora-section={dataAutotaggingFloraSection}
            ref={ref}
            status={status}
            placeholder={placeholder}
            labelPosition={labelPosition}
            clicked={clicked}
            onChange={handleOnChange}
            onMouseDownCapture={handleOnMouseDownCaptureTextarea}
            onBlur={handleOnBlur}
            disabled={disabled}
            required={required}
            maxLength={maxLength}
            surfaceColor={surfaceColor}
            backgroundStyle={backgroundStyle}
            readOnly={readOnly || !!ariaDisabled || status === 'loading'}
            autotagging-flora={autotaggingFlora}
            aria-disabled={ariaDisabled}
            aria-describedby={`helpertext-${id}`}
            aria-invalid={status === 'error'}
          >
            {children}
          </StyledTextarea>
          <MessagesWrapper>
            {helperText && (
              <FloraHelperText
                id={`helpertext-${id}`}
                status={disabled || ariaDisabled ? 'disabled' : status}
                surfaceColor={surfaceColor}
              >
                {helperText}
              </FloraHelperText>
            )}
            {maxLength && (
              <CharacterCount surfaceColor={surfaceColor}>
                {charCount}/{maxLength}
              </CharacterCount>
            )}
          </MessagesWrapper>
        </FocusWrapper>
      </>
    )
  },
)
